/**
 * SPECIES — what lives in the sea, and the only thing that decides whether you
 * can catch it: your level.
 *
 * DESIGN LAW, from CONTRACT.md section 6: a species gates on level, never on a
 * clock and never on a purchase. The Species record below therefore has nowhere
 * to put a price, a duration, an unlock date, an event window or an entitlement
 * id, and species.test.ts fails if such a field ever appears. Absence enforced
 * by a type is absence; absence by convention is a comment.
 *
 * Cadence, MEASURED against the depth bar in gauntlet/refs/bar-depth.json:
 * Melvor's Fishing has 19 unlock levels in 1-99 with a largest gap of exactly 10
 * levels. Reelin has 24 unlock levels with a largest gap of 5. We are EXCEEDING
 * that bar, not matching it, and species.test.ts asserts the gap.
 *
 * Three-outcome casts: every species here has BOTH a nonzero junk chance and a
 * nonzero special chance, so every cast in the game is genuinely a three-way
 * roll. This also exceeds the bar: 5 of Melvor's 15 areas have 0% special and
 * all 7 Abyssal areas have 0% junk, making a cast there a two-outcome roll.
 *
 * ---------------------------------------------------------------------------
 * THE TABLE IS A BALANCING SURFACE. OWNER RULING, CONTRACT.md section 1.5l(b).
 * ---------------------------------------------------------------------------
 *
 * "SUNFISH DOMINATING ON TOTAL WEIGHT IS NOT ACCEPTABLE... No single fish may
 * lead on every measure, even one nobody plays for. The species table becomes a
 * balancing surface: the biggest must not also be the most frequent and the most
 * valuable."
 *
 * THE PRINCIPLE THIS TABLE IS BALANCED ON, stated before any number moved:
 * EVERY SPECIES IS THE BEST FISH IN THE SEA BY EXACTLY ONE MEASURE AND NO
 * SPECIES HOLDS TWO — a fish is a population with a typical mass and a record
 * mass, a cadence, and a price, and those four crowns belong to four different
 * animals.
 *
 * The four measures a player can read off a species, and who holds each. Which
 * fish holds which is DERIVED from the principle plus the constraints already in
 * this file; the grams are MODELLED on real populations and are not measured
 * from anything:
 *
 *   MOST FREQUENT   shortest cast, so the sea gives you one soonest.
 *                   MINNOW, castTicks 4. Forced: `castTicks` rises with the gate
 *                   and species.test.ts asserts xp per tick rises with it, so the
 *                   first species is the fastest and nothing else can be.
 *   MOST VALUABLE   highest coins. MOONFISH, 486. Forced the same way: coins rise
 *                   strictly with the gate, so the last species is the dearest.
 *   HEAVIEST HAUL   highest TYPICAL mass, which is what total landed weight
 *                   follows over a run. BLUEFIN TUNA. A mature bluefin school is
 *                   uniformly enormous; there are no small adults in it.
 *   BIGGEST FISH    highest RECORD mass, the heaviest single specimen a run can
 *                   land. SUNFISH. The ocean sunfish is the heaviest bony fish
 *                   ever recorded and the typical one is a fraction of that: it
 *                   is a lottery, not a school.
 *
 * The first two crowns were already forced by rules this file had before the
 * ruling. The ruling is therefore satisfied by moving the last two apart, and
 * the only column that had to move is the weight band.
 *
 * HOW A WEIGHT BAND IS BUILT, so the two crowns are separable at all. A band is
 * two numbers, `[minWeightG, maxWeightG]`, and `randRange` draws uniformly and
 * inclusively between them. Read it instead as a TYPICAL mass t and a SPREAD s:
 *
 *     minWeightG = t * (1 - s)      maxWeightG = t * (1 + s)
 *
 * so t is exactly the mean of the draw and t*(1+s) is exactly the record it can
 * produce. Total landed weight follows t; the record follows t*(1+s). A narrow
 * band with a high t beats a wide band with a lower t on the haul and loses to
 * it on the record. That is the whole trade, and it is why the two crowns can be
 * held by different fish rather than always by the largest number in the column.
 *
 * WHAT MOVED, AND NOTHING ELSE DID. Twenty-one rows are untouched; their t and s
 * are whatever their existing band already implied. Three rows moved, each for a
 * reason the ruling names:
 *
 *   bluefin   60,000-250,000  ->  238,000-442,000   t 340,000  s 0.30
 *             Takes the HEAVIEST HAUL crown. A school of uniformly large adults,
 *             so the spread is the narrowest at the top of the table.
 *   sunfish   250,000-1,000,000 -> 29,000-551,000   t 290,000  s 0.90
 *             Keeps the BIGGEST FISH crown and gives up the haul. The widest
 *             spread in the table: most are modest, a few are enormous. Before
 *             this it held both crowns, which is the defect the ruling names.
 *   moonfish  100,000-600,000 ->  126,000-434,000   t 280,000  s 0.55
 *             Holds MOST VALUABLE, so by the principle it may hold nothing else.
 *             It was second on typical mass and second on the record; now it is
 *             behind bluefin on one and behind both on the other.
 *
 * WEIGHT DOES NOT RISE MONOTONICALLY WITH THE GATE, AND DID NOT BEFORE EITHER —
 * lanternshark at level 97 was always lighter than opah at 71. The ladder is
 * about xp, coins and odds, which DO rise strictly with the gate and are
 * asserted to. Size is a property of the animal, and making it a second reward
 * curve is what let one fish lead everything.
 *
 * MEASURED CONSEQUENCE, `node tools/lanes/progression/dominance.mjs`, 20,000
 * ticks x 3 seeds at level 99: the aggregate axis set goes from RED, dominated
 * by sunfish, to GREEN. `--plant flat-record` and `--plant flat-weight` each put
 * a dominator straight back, which is what says the green is a trade between two
 * live axes and not a tie.
 */

export interface Species {
  /** Stable id. Appears in saves forever; never renamed, only added to. */
  readonly id: string;
  readonly name: string;
  /** The ONLY gate. Integer 1-99. */
  readonly minLevel: number;
  /** Base ticks per cast at skill 0. Integer, >= 1. */
  readonly castTicks: number;
  /** XP awarded for a fish or a special. Junk awards none. Integer. */
  readonly xp: number;
  /** Coins for a fish. A special pays SPECIAL_COIN_MULTIPLIER times this. Integer. */
  readonly coins: number;
  /** Weight is drawn uniformly in [minWeightG, maxWeightG]. Grams, integer — the
   *  save never stores a float, so a total weight can never drift. */
  readonly minWeightG: number;
  readonly maxWeightG: number;
  /** Junk chance in parts per thousand. Integer, > 0. */
  readonly junkPer1000: number;
  /** Special chance in parts per thousand. Integer, > 0. */
  readonly specialPer1000: number;
}

/** A special pays this multiple of the species' coin value. */
export const SPECIAL_COIN_MULTIPLIER = 5;

export const SPECIES: readonly Species[] = [
  { id: 'minnow',       name: 'Minnow',        minLevel:  1, castTicks:  4, xp:   5, coins:   2, minWeightG:    20, maxWeightG:    90, junkPer1000: 90, specialPer1000: 10 },
  { id: 'sardine',      name: 'Sardine',       minLevel:  3, castTicks:  5, xp:   7, coins:   3, minWeightG:    40, maxWeightG:   160, junkPer1000: 86, specialPer1000: 11 },
  { id: 'herring',      name: 'Herring',       minLevel:  6, castTicks:  5, xp:   8, coins:   4, minWeightG:    90, maxWeightG:   320, junkPer1000: 82, specialPer1000: 12 },
  { id: 'mackerel',     name: 'Mackerel',      minLevel:  9, castTicks:  6, xp:  11, coins:   6, minWeightG:   200, maxWeightG:   700, junkPer1000: 78, specialPer1000: 13 },
  { id: 'pilchard',     name: 'Pilchard',      minLevel: 13, castTicks:  6, xp:  12, coins:   7, minWeightG:   120, maxWeightG:   420, junkPer1000: 74, specialPer1000: 14 },
  { id: 'grey_mullet',  name: 'Grey Mullet',   minLevel: 17, castTicks:  7, xp:  15, coins:   9, minWeightG:   400, maxWeightG:  1600, junkPer1000: 70, specialPer1000: 15 },
  { id: 'sea_bream',    name: 'Sea Bream',     minLevel: 21, castTicks:  7, xp:  16, coins:  11, minWeightG:   500, maxWeightG:  2200, junkPer1000: 66, specialPer1000: 16 },
  { id: 'pollock',      name: 'Pollock',       minLevel: 26, castTicks:  8, xp:  19, coins:  14, minWeightG:   700, maxWeightG:  3400, junkPer1000: 62, specialPer1000: 17 },
  { id: 'red_snapper',  name: 'Red Snapper',   minLevel: 31, castTicks:  9, xp:  22, coins:  18, minWeightG:   900, maxWeightG:  4800, junkPer1000: 58, specialPer1000: 18 },
  { id: 'sea_bass',     name: 'Sea Bass',      minLevel: 36, castTicks: 10, xp:  26, coins:  23, minWeightG:  1200, maxWeightG:  6500, junkPer1000: 54, specialPer1000: 19 },
  { id: 'cod',          name: 'Cod',           minLevel: 41, castTicks: 11, xp:  30, coins:  29, minWeightG:  1800, maxWeightG:  9000, junkPer1000: 50, specialPer1000: 20 },
  { id: 'grouper',      name: 'Grouper',       minLevel: 46, castTicks: 12, xp:  34, coins:  36, minWeightG:  3000, maxWeightG: 20000, junkPer1000: 47, specialPer1000: 21 },
  { id: 'amberjack',    name: 'Amberjack',     minLevel: 51, castTicks: 13, xp:  39, coins:  45, minWeightG:  4000, maxWeightG: 26000, junkPer1000: 44, specialPer1000: 22 },
  { id: 'wahoo',        name: 'Wahoo',         minLevel: 56, castTicks: 14, xp:  44, coins:  56, minWeightG:  5000, maxWeightG: 32000, junkPer1000: 41, specialPer1000: 23 },
  { id: 'yellowfin',    name: 'Yellowfin Tuna', minLevel: 61, castTicks: 15, xp:  50, coins:  70, minWeightG:  8000, maxWeightG: 60000, junkPer1000: 38, specialPer1000: 24 },
  { id: 'swordfish',    name: 'Swordfish',     minLevel: 66, castTicks: 16, xp:  57, coins:  87, minWeightG: 20000, maxWeightG: 120000, junkPer1000: 35, specialPer1000: 25 },
  { id: 'opah',         name: 'Opah',          minLevel: 71, castTicks: 17, xp:  65, coins: 108, minWeightG: 25000, maxWeightG: 90000, junkPer1000: 33, specialPer1000: 26 },
  { id: 'bluefin',      name: 'Bluefin Tuna',  minLevel: 76, castTicks: 18, xp:  74, coins: 134, minWeightG: 238000, maxWeightG: 442000, junkPer1000: 31, specialPer1000: 27 },
  { id: 'marlin',       name: 'Blue Marlin',   minLevel: 81, castTicks: 19, xp:  85, coins: 166, minWeightG: 80000, maxWeightG: 400000, junkPer1000: 29, specialPer1000: 28 },
  { id: 'oarfish',      name: 'Oarfish',       minLevel: 86, castTicks: 20, xp:  98, coins: 206, minWeightG: 90000, maxWeightG: 270000, junkPer1000: 27, specialPer1000: 29 },
  { id: 'sunfish',      name: 'Sunfish',       minLevel: 90, castTicks: 21, xp: 112, coins: 255, minWeightG:  29000, maxWeightG: 551000, junkPer1000: 25, specialPer1000: 30 },
  { id: 'coelacanth',   name: 'Coelacanth',    minLevel: 94, castTicks: 22, xp: 128, coins: 316, minWeightG: 30000, maxWeightG: 90000, junkPer1000: 23, specialPer1000: 31 },
  { id: 'lanternshark', name: 'Lanternshark',  minLevel: 97, castTicks: 23, xp: 145, coins: 392, minWeightG: 15000, maxWeightG: 45000, junkPer1000: 21, specialPer1000: 32 },
  { id: 'moonfish',     name: 'Moonfish',      minLevel: 99, castTicks: 24, xp: 165, coins: 486, minWeightG: 126000, maxWeightG: 434000, junkPer1000: 20, specialPer1000: 33 },
];

const BY_ID: ReadonlyMap<string, Species> = new Map(SPECIES.map((s) => [s.id, s]));

/** Lookup by id. Returns undefined for an id this build does not know. */
export function speciesById(id: string): Species | undefined {
  return BY_ID.get(id);
}

/** True if this build knows the id. Used to keep unknown save keys out of the rate engine. */
export function isKnownSpecies(id: string): boolean {
  return BY_ID.has(id);
}

/**
 * Everything a player of this level may catch.
 * Level is the only argument, because level is the only gate.
 */
export function unlockedSpecies(level: number): readonly Species[] {
  const l = Number.isFinite(level) ? Math.trunc(level) : 1;
  return SPECIES.filter((s) => s.minLevel <= l);
}

/**
 * The default target when the player has not chosen one: the highest-level
 * species they have unlocked. Always returns a species — SPECIES[0] gates at
 * level 1, so a level-1 player is never left with nothing to fish for.
 */
export function bestUnlockedSpecies(level: number): Species {
  const l = Number.isFinite(level) ? Math.trunc(level) : 1;
  let best: Species = SPECIES[0]!;
  for (const s of SPECIES) {
    if (s.minLevel <= l && s.minLevel >= best.minLevel) best = s;
  }
  return best;
}
